import React, { useState, useMemo, useEffect } from "react";
import {
  FormControl,
  Select,
  MenuItem,
  TextField,
  Checkbox,
  Autocomplete,
  Tooltip,
} from "@mui/material";
import GlobalIcon from "../GlobalIcon/GlobalIcon";
import {
  clampQtyLots,
  deriveAbsDisclosedQty,
  clampPriceToCircuitLimits,
  constrainCircuitPriceInput,
  shouldApplyCircuitPriceLimit,
  circuitPriceLimitReason,
  circuitBoundHit,
  priceVsLtpWarning,
  isLimitOrderUiType,
  usesLotSizeQuantity,
  isCashEquityExchange,
  isDerivativesExchange,
  isStrikeEditable,
  isExpiryEditable,
  isCategoryEditable,
} from "../../pages/OrderInitiatePage/utils";
import styles from "./ScriptComponent.module.css";

/** Set on document.body on Tab; cleared on pointerdown — focus rings in CSS only apply with this */
let scriptTabFocusBodyListenerCount = 0;

const SelectDropdownIcon = (props) => <GlobalIcon iconName="SelectDropdownArrow" {...props} />;

function QuoteMetricBlock({ item }) {
  return (
    <div className={styles.quoteBlock}>
      <div
        className={`${styles.quoteValue}${item.highlight ? ` ${styles.quoteValueHighlight}` : ""}${item.positive ? ` ${styles.quotePositive}` : ""}${item.negative ? ` ${styles.quoteNegative}` : ""}`}
      >
        {item.value}
      </div>
      <div className={styles.quoteLabel}>{item.label}</div>
    </div>
  );
}

function renderQuoteMetrics(quoteMetrics) {
  return quoteMetrics.map((item) => (
    <QuoteMetricBlock key={item.label} item={item} />
  ));
}

const SCRIPT_SELECT_MENU_PROPS = {
  disablePortal: false,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  MenuListProps: { dense: true },
  slotProps: {
    paper: {
      className: "scriptSelectMenuPaper",
      sx: { maxHeight: 280 },
    },
  },
};

function cycleOptionList(options, currentValue, key) {
  if (!options?.length || options.length < 2) return undefined;
  const idx = options.indexOf(currentValue);
  const i = idx >= 0 ? idx : 0;
  if (key === "ArrowDown") return options[(i + 1) % options.length];
  if (key === "ArrowUp") return options[(i - 1 + options.length) % options.length];
  return undefined;
}

function mergeSelectMenuPropsWithArrow(onMenuListKeyDown) {
  return {
    ...SCRIPT_SELECT_MENU_PROPS,
    MenuListProps: {
      ...SCRIPT_SELECT_MENU_PROPS.MenuListProps,
      onKeyDown: (e) => onMenuListKeyDown(e),
    },
  };
}

/** Prefer exact match, then prefix, then contains (Trading Symbol / Strike). */
function filterScripSearchOptions(options, { inputValue }) {
  const q = String(inputValue ?? "").trim().toLowerCase();
  if (!q) return options;
  const matched = options.filter((opt) => String(opt).toLowerCase().includes(q));
  matched.sort((a, b) => {
    const al = String(a).toLowerCase();
    const bl = String(b).toLowerCase();
    const aExact = al === q ? 0 : 1;
    const bExact = bl === q ? 0 : 1;
    if (aExact !== bExact) return aExact - bExact;
    const aStarts = al.startsWith(q) ? 0 : 1;
    const bStarts = bl.startsWith(q) ? 0 : 1;
    if (aStarts !== bStarts) return aStarts - bStarts;
    return al.localeCompare(bl, undefined, { numeric: true });
  });
  return matched;
}

function findExactScripOptionMatch(options, inputValue) {
  const q = String(inputValue ?? "").trim().toLowerCase();
  if (!q || !options?.length) return null;
  return options.find((opt) => String(opt).toLowerCase() === q) ?? null;
}

// Keep legacy names so any stale bundles/references resolve cleanly.
const filterTradingSymbolOptions = filterScripSearchOptions;
const findExactTradingSymbolMatch = findExactScripOptionMatch;

const SEARCHABLE_SCRIP_FIELDS = new Set(["tradingSymbol", "strike"]);

const PRODUCT_OPTIONS = ["DELIVERY", "INTRADAY", "MARGIN"];

/** Format API expiry "2026-02-24" to display "24-02-2026" */
function formatExpiryDisplay(apiDate) {
  if (!apiDate || typeof apiDate !== "string") return apiDate;
  const [y, m, d] = apiDate.split("-");
  return d && m && y ? `${d}-${m}-${y}` : apiDate;
}

function getRawScripField(selection, field) {
  if (!selection) return undefined;
  switch (field) {
    case "exchange":
      return selection.exchange;
    case "scripType":
      return selection.segment;
    case "tradingSymbol":
      return selection.symbol;
    case "category":
      return selection.option_type;
    case "strike":
      return selection.strike_price;
    case "expiry":
      return selection.expiry_date;
    default:
      return undefined;
  }
}

const SCRIP_DROPDOWN_FIELDS = [
  { label: "Exchange", field: "exchange" },
  { label: "Scrip Type", field: "scripType" },
  { label: "Trading Symbol", field: "tradingSymbol" },
  { label: "Category", field: "category" },
  { label: "Strike", field: "strike" },
  { label: "Expiry", field: "expiry" },
];

const EMPTY_SCRIP_VALUES = {
  exchange: "",
  scripType: "",
  tradingSymbol: "",
  category: "",
  strike: "",
  expiry: "",
};

const ORDER_TYPE_UI_OPTIONS = ["Bid/Ask", "Limit Order"];

function ScriptComponent({
  id,
  scriptIndex = 0,
  dropdownOptions,
  selection,
  onSelectionChange,
  onDelete,
  onAddScript,
  animateIn,
  selected,
  onSelectedChange,
  productOptions = [],
  orderTypeOptions = [],
  validityOptions = [],
  quoteMetrics = [],
  orderBookRows = [],
  orderBookBuyRows,
  orderBookSellRows,
  bidFooter = [],
  askFooter = [],
  headerSymbol,
  marginRequired,
  defaultPrice,
  defaultQtyLots,
  defaultAbsDisQty,
  quoteLotSize,
  lowerCircuitLimit,
  upperCircuitLimit,
}) {
  const displayHeaderSymbol = headerSymbol ?? "—";
  const displayMargin = marginRequired != null && marginRequired !== "" ? `MARGIN REQUIRED- ${marginRequired}` : "—";
  const buyRows = orderBookBuyRows ?? orderBookRows;
  const sellRows = orderBookSellRows ?? orderBookRows;
  const isControlled = selection != null && typeof onSelectionChange === "function";
  const opts = useMemo(() => {
    if (!dropdownOptions) return null;
    const ex = dropdownOptions.unique_exchange_options || [];
    const seg = dropdownOptions.unique_segment_options || [];
    const sym = dropdownOptions.unique_symbol_options || [];
    const cat = dropdownOptions.unique_option_type_options || [];
    const strikes = dropdownOptions.unique_strike_price_options ?? [];
    const exp = dropdownOptions.unique_expiry_date_options || [];
    return {
      exchange: ex,
      scripType: seg,
      tradingSymbol: sym,
      category: cat,
      strike: strikes.map((s) => String(s)),
      expiry: exp.map((e) => formatExpiryDisplay(e)),
    };
  }, [dropdownOptions]);

  const [localScrip, setLocalScrip] = useState(EMPTY_SCRIP_VALUES);

  const exchangeValue = useMemo(() => {
    const raw = isControlled ? selection?.exchange : localScrip.exchange;
    return String(raw ?? "").trim();
  }, [isControlled, selection?.exchange, localScrip.exchange]);

  const getDisplayValue = (field) => {
    if (isControlled) {
      const raw = getRawScripField(selection, field);
      // Respect explicit "" so cleared fields stay empty (do not substitute first API option)
      if (raw !== undefined && raw !== null) {
        if (field === "expiry") return raw === "" ? "" : formatExpiryDisplay(raw);
        if (field === "strike") return raw === "" ? "" : String(raw);
        return String(raw);
      }
      const fromOpts = opts?.[field]?.[0];
      return fromOpts ?? localScrip[field] ?? "";
    }
    return localScrip[field];
  };

  const getOptionsForField = (field) => opts?.[field] ?? [];

  const getScripSelectValue = (field) => {
    const v = getDisplayValue(field);
    const options = getOptionsForField(field);
    if (!options.length) return v ?? "";
    // Cascade blanks must stay blank until the dropdown API returns new options.
    if (v === "" || v == null) return "";
    if (!options.includes(v)) return "";
    return v;
  };

  /** Exchange / segment shown in the UI (may differ from raw state before sync). */
  const displayedExchange = getScripSelectValue("exchange");
  const displayedSegment = getScripSelectValue("scripType");

  /** Strike & expiry follow F&O exchange (BFO/NFO/…), not stale segment from a prior NSE row. */
  const strikeDisabled = !isStrikeEditable(displayedExchange, displayedSegment);
  const expiryDisabled = !isExpiryEditable(displayedExchange, displayedSegment);
  const categoryDisabled = !isCategoryEditable(displayedExchange, displayedSegment);

  /** Lot math / locked disclosed qty when quote API lot_size > 1. */
  const absDisQtyDisabledForLots = usesLotSizeQuantity(quoteLotSize);

  const handleScripChange = (field, value) => {
    if (isControlled) {
      onSelectionChange(scriptIndex, field, value);
      return;
    }
    setLocalScrip((prev) => ({ ...prev, [field]: value }));
  };

  const handleScripArrowKey = (field, e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const options = getOptionsForField(field);
    const current = getScripSelectValue(field);
    const next = cycleOptionList(options, current, e.key);
    if (next !== undefined && next !== current) {
      e.preventDefault();
      e.stopPropagation();
      handleScripChange(field, next);
    }
  };

  useEffect(() => {
    if (!opts || isControlled) return;
    setLocalScrip((prev) => {
      const next = { ...prev };
      SCRIP_DROPDOWN_FIELDS.forEach(({ field }) => {
        if (field === "strike" && strikeDisabled) return;
        if (field === "expiry" && expiryDisabled) return;
        if (field === "category" && categoryDisabled) return;
        const options = opts[field];
        if (!options?.length) return;
        const cur = prev[field];
        if (cur === "" || cur == null) return;
        if (!options.includes(cur)) next[field] = options[0];
      });
      return next;
    });
  }, [opts, isControlled, strikeDisabled, expiryDisabled, categoryDisabled]);

  const [localState, setLocalState] = useState({
    transactionType: "Buy",
    qtyLots: "",
    absDisQty: "",
    productType: "",
    orderType: "",
    price: "",
    validity: "",
  });

  const getProp = (key) => isControlled && selection?.[key] !== undefined ? selection[key] : localState[key];
  const setProp = (key, val) => {
    if (isControlled) onSelectionChange(scriptIndex, key, val);
    else setLocalState(prev => ({ ...prev, [key]: val }));
  };

  const transactionType = getProp("transactionType");
  const setTransactionType = (val) => setProp("transactionType", val);
  const productType = getProp("productType");
  const setProductType = (val) => setProp("productType", val);
  const orderType = getProp("orderType");
  const setOrderType = (val) => setProp("orderType", val);
  const validity = getProp("validity");
  const setValidity = (val) => setProp("validity", val);
  const parentQtyLots = getProp("qtyLots");
  const parentAbsDisQty = getProp("absDisQty");
  const parentPrice = getProp("price");

  const [localText, setLocalText] = useState({
    qtyLots: parentQtyLots,
    absDisQty: parentAbsDisQty,
    price: parentPrice,
  });

  const qtyLots = localText.qtyLots;
  const absDisQty = localText.absDisQty;
  const price = localText.price;

  const orderTypeRef = React.useRef(orderType);
  orderTypeRef.current = orderType;
  const lowerCircuitRef = React.useRef(lowerCircuitLimit);
  lowerCircuitRef.current = lowerCircuitLimit;
  const upperCircuitRef = React.useRef(upperCircuitLimit);
  upperCircuitRef.current = upperCircuitLimit;
  const circuitExchange = displayedExchange || exchangeValue;
  const circuitExchangeRef = React.useRef(circuitExchange);
  circuitExchangeRef.current = circuitExchange;
  const applyCircuitPriceLimit = shouldApplyCircuitPriceLimit(
    circuitExchange,
    orderType,
    lowerCircuitLimit,
    upperCircuitLimit
  );
  const [circuitTooltipOpen, setCircuitTooltipOpen] = useState(false);
  const [circuitTooltipText, setCircuitTooltipText] = useState("");
  const [priceEmptyTooltipOpen, setPriceEmptyTooltipOpen] = useState(false);
  const circuitTooltipForcedRef = React.useRef(false);
  const circuitTooltipTimerRef = React.useRef(null);
  const defaultPriceRef = React.useRef(defaultPrice);
  defaultPriceRef.current = defaultPrice;
  const transactionTypeRef = React.useRef(transactionType);
  transactionTypeRef.current = transactionType;

  const hidePriceTooltip = React.useCallback(() => {
    circuitTooltipForcedRef.current = false;
    if (circuitTooltipTimerRef.current) {
      clearTimeout(circuitTooltipTimerRef.current);
      circuitTooltipTimerRef.current = null;
    }
    setCircuitTooltipOpen(false);
    setCircuitTooltipText("");
  }, []);

  const hidePriceEmptyTooltip = React.useCallback(() => {
    setPriceEmptyTooltipOpen(false);
  }, []);

  const showPriceHintTooltip = React.useCallback((text) => {
    if (!text) {
      hidePriceTooltip();
      return;
    }
    circuitTooltipForcedRef.current = true;
    setCircuitTooltipText(text);
    setCircuitTooltipOpen(true);
    if (circuitTooltipTimerRef.current) clearTimeout(circuitTooltipTimerRef.current);
    circuitTooltipTimerRef.current = setTimeout(() => {
      circuitTooltipForcedRef.current = false;
      setCircuitTooltipOpen(false);
      circuitTooltipTimerRef.current = null;
    }, 4000);
  }, [hidePriceTooltip]);

  const showCircuitReasonTooltip = React.useCallback((hit) => {
    showPriceHintTooltip(circuitPriceLimitReason(lowerCircuitRef.current, upperCircuitRef.current, hit));
  }, [showPriceHintTooltip]);

  const evaluatePriceWarningAfterInput = React.useCallback((priceVal) => {
    const trimmed = String(priceVal ?? "").trim();
    if (trimmed === "" || /[.,]$/.test(trimmed)) {
      hidePriceTooltip();
      return;
    }
    if (
      shouldApplyCircuitPriceLimit(
        circuitExchangeRef.current,
        orderTypeRef.current,
        lowerCircuitRef.current,
        upperCircuitRef.current
      )
    ) {
      const hit = circuitBoundHit(priceVal, lowerCircuitRef.current, upperCircuitRef.current);
      if (hit) {
        showPriceHintTooltip(circuitPriceLimitReason(lowerCircuitRef.current, upperCircuitRef.current, hit));
        return;
      }
    }
    const ltpWarning = priceVsLtpWarning(
      priceVal,
      defaultPriceRef.current,
      transactionTypeRef.current,
      orderTypeRef.current
    );
    if (ltpWarning) {
      showPriceHintTooltip(ltpWarning);
      return;
    }
    hidePriceTooltip();
  }, [hidePriceTooltip, showPriceHintTooltip]);

  React.useEffect(() => {
    return () => {
      if (circuitTooltipTimerRef.current) clearTimeout(circuitTooltipTimerRef.current);
    };
  }, []);
  const priceStrRef = React.useRef("");
  priceStrRef.current = String(price ?? "");

  const userClearedQtyRef = React.useRef(false);
  const userClearedAbsDisQtyRef = React.useRef(false);
  const userClearedPriceRef = React.useRef(false);
  /** Once the user edits a field, keep it — do not overwrite from quote polls. */
  const userEditedPriceRef = React.useRef(false);
  const userEditedQtyRef = React.useRef(false);
  const userEditedAbsDisQtyRef = React.useRef(false);
  const priceInputFocusedRef = React.useRef(false);
  const typingTimers = React.useRef({});

  /** Reset price/qty edit locks when the instrument changes — not on strike/expiry auto-fill or LTP polls. */
  const instrumentKey = [
    selection?.exchange ?? localScrip.exchange,
    selection?.segment ?? localScrip.scripType,
    selection?.symbol ?? localScrip.tradingSymbol,
  ].join("|");

  useEffect(() => {
    setLocalText((p) => ({ ...p, qtyLots: parentQtyLots }));
  }, [parentQtyLots]);
  useEffect(() => {
    setLocalText((p) => ({ ...p, absDisQty: parentAbsDisQty }));
  }, [parentAbsDisQty]);
  useEffect(() => {
    if (userEditedPriceRef.current) return;
    setLocalText((p) => ({ ...p, price: parentPrice }));
  }, [parentPrice]);

  const qtyLotsFallback = defaultQtyLots != null && String(defaultQtyLots).trim() !== "" ? defaultQtyLots : 1;

  const handleTextChange = (key, val) => {
    let nextVal = val;
    if (key === "price") {
      nextVal = constrainCircuitPriceInput(
        val,
        orderTypeRef.current,
        lowerCircuitRef.current,
        upperCircuitRef.current,
        circuitExchangeRef.current
      );
      if (nextVal !== val) {
        showCircuitReasonTooltip(
          circuitBoundHit(val, lowerCircuitRef.current, upperCircuitRef.current)
        );
      }
    }
    setLocalText((prev) => ({ ...prev, [key]: nextVal }));
    if (key === "qtyLots") {
      userClearedQtyRef.current = val === "";
      userEditedQtyRef.current = true;
    }
    if (key === "absDisQty") {
      userClearedAbsDisQtyRef.current = val === "";
      userEditedAbsDisQtyRef.current = true;
    }
    if (key === "price") {
      userClearedPriceRef.current = nextVal === "";
      userEditedPriceRef.current = true;
      if (nextVal !== "") hidePriceEmptyTooltip();
      if (nextVal === "") {
        setPriceEmptyTooltipOpen(true);
        hidePriceTooltip();
      }
    }
    if (typingTimers.current[key]) clearTimeout(typingTimers.current[key]);
    typingTimers.current[key] = setTimeout(() => {
      if (key === "qtyLots") {
        if (val === "") {
          setLocalText((prev) => ({ ...prev, qtyLots: "", absDisQty: "" }));
          setProp("qtyLots", "");
          setProp("absDisQty", "");
          return;
        }
        userClearedQtyRef.current = false;
        const c = String(clampQtyLots(val, qtyLotsFallback));
        const derived = deriveAbsDisclosedQty(c, quoteLotSize);
        const lockAbsFromLots = usesLotSizeQuantity(quoteLotSize) || !userEditedAbsDisQtyRef.current;
        setLocalText((prev) => ({
          ...prev,
          qtyLots: c,
          ...(derived !== "" && lockAbsFromLots ? { absDisQty: derived } : {}),
        }));
        setProp("qtyLots", c);
        if (derived !== "" && lockAbsFromLots) {
          userClearedAbsDisQtyRef.current = false;
          setProp("absDisQty", derived);
        }
        return;
      }
      if (key === "price") {
        setProp("price", nextVal);
        if (nextVal === val) {
          evaluatePriceWarningAfterInput(nextVal);
        }
        return;
      }
      setProp(key, val);
    }, 400);
  };

  const handleQtyLotsBlur = () => {
    if (typingTimers.current.qtyLots) {
      clearTimeout(typingTimers.current.qtyLots);
      typingTimers.current.qtyLots = null;
    }
    if (qtyLots === "" && userClearedQtyRef.current) {
      setProp("qtyLots", "");
      setProp("absDisQty", "");
      setLocalText((prev) => ({ ...prev, absDisQty: "" }));
      return;
    }
    const c = String(clampQtyLots(qtyLots, qtyLotsFallback));
    const derived = deriveAbsDisclosedQty(c, quoteLotSize);
    const lockAbsFromLots = usesLotSizeQuantity(quoteLotSize) || !userEditedAbsDisQtyRef.current;
    setLocalText((prev) => ({
      ...prev,
      qtyLots: c,
      ...(derived !== "" && lockAbsFromLots ? { absDisQty: derived } : {}),
    }));
    setProp("qtyLots", c);
    if (derived !== "" && lockAbsFromLots) {
      userClearedAbsDisQtyRef.current = false;
      setProp("absDisQty", derived);
    }
  };

  const handlePriceBlur = () => {
    priceInputFocusedRef.current = false;
    if (typingTimers.current.price) {
      clearTimeout(typingTimers.current.price);
      typingTimers.current.price = null;
    }
    const raw = price ?? "";
    const trimmed = String(raw).trim();
    let next = String(raw);
    if (trimmed === "") {
      next = "";
      hidePriceTooltip();
      setPriceEmptyTooltipOpen(true);
    } else {
      setPriceEmptyTooltipOpen(false);
      next = clampPriceToCircuitLimits(
        raw,
        orderType,
        lowerCircuitLimit,
        upperCircuitLimit,
        circuitExchange
      );
      setPriceEmptyTooltipOpen(false);
    }
    if (next !== String(raw)) {
      setLocalText((prev) => ({ ...prev, price: next }));
      showCircuitReasonTooltip(circuitBoundHit(raw, lowerCircuitLimit, upperCircuitLimit));
    } else {
      evaluatePriceWarningAfterInput(next);
    }
    setProp("price", next);
  };

  const setQtyLots = (val) => handleTextChange("qtyLots", val);
  const setAbsDisQty = (val) => handleTextChange("absDisQty", val);
  const setPrice = (val) => handleTextChange("price", val);

  const [localHeaderSelected, setLocalHeaderSelected] = useState(false);
  const isHeaderCheckboxControlled = typeof selected === "boolean" && typeof onSelectedChange === "function";
  const headerSelected = isHeaderCheckboxControlled ? selected : localHeaderSelected;
  const setHeaderSelected = isHeaderCheckboxControlled
    ? (checked) => onSelectedChange(checked)
    : setLocalHeaderSelected;

  useEffect(() => {
    if (!isLimitOrderUiType(orderType)) hidePriceTooltip();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (productType === "") setProductType("DELIVERY"); }, [productType]);
  useEffect(() => {
    if (validityOptions?.length > 0) {
      const currentValidity = validity || "";
      if (!currentValidity || !validityOptions.includes(currentValidity)) {
        setValidity(validityOptions[0]);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validityOptions, validity]);
  /* Poll sync: get_quote_data updates last_price → defaultPrice.
     Seed Price from LTP until the user types (or clears) it — never overwrite their input. */
  useEffect(() => {
    if (defaultPrice == null || defaultPrice === "") return;
    if (userClearedPriceRef.current) return;
    if (userEditedPriceRef.current) return;
    if (priceInputFocusedRef.current) return;
    if (typingTimers.current.price) return;
    let nextPrice = defaultPrice;
    if (applyCircuitPriceLimit) {
      nextPrice = clampPriceToCircuitLimits(
        defaultPrice,
        orderType,
        lowerCircuitLimit,
        upperCircuitLimit,
        circuitExchange
      );
    }
    setLocalText((prev) => {
      if (prev.price === nextPrice) return prev;
      return { ...prev, price: nextPrice };
    });
    if (isControlled) {
      onSelectionChange(scriptIndex, "price", nextPrice);
    } else {
      setLocalState((prev) => ({ ...prev, price: nextPrice }));
    }
  }, [
    defaultPrice,
    orderType,
    lowerCircuitLimit,
    upperCircuitLimit,
    circuitExchange,
    applyCircuitPriceLimit,
    isControlled,
    scriptIndex,
    onSelectionChange,
  ]);

  /* Re-clamp when switching to Limit on NSE/BSE — not when circuit bounds refresh from polls. */
  useEffect(() => {
    if (!applyCircuitPriceLimit) return;
    const raw = priceStrRef.current;
    const s = String(raw ?? "").trim();
    if (s === "") return;
    const clamped = clampPriceToCircuitLimits(
      raw,
      orderType,
      lowerCircuitLimit,
      upperCircuitLimit,
      circuitExchange
    );
    if (clamped === String(raw)) return;
    setLocalText((prev) => ({ ...prev, price: clamped }));
    if (isControlled) {
      onSelectionChange(scriptIndex, "price", clamped);
    } else {
      setLocalState((prev) => ({ ...prev, price: clamped }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- circuit bound polls must not overwrite user price
  }, [orderType, circuitExchange, applyCircuitPriceLimit, isControlled, scriptIndex, onSelectionChange]);

  useEffect(() => {
    userClearedPriceRef.current = false;
    userEditedPriceRef.current = false;
    userClearedQtyRef.current = false;
    userEditedQtyRef.current = false;
    userClearedAbsDisQtyRef.current = false;
    userEditedAbsDisQtyRef.current = false;
  }, [instrumentKey]);

  useEffect(() => {
    if (
      defaultQtyLots == null ||
      defaultQtyLots === "" ||
      qtyLots !== "" ||
      userClearedQtyRef.current ||
      userEditedQtyRef.current
    ) {
      return;
    }
    const c = String(clampQtyLots(defaultQtyLots, 1));
    const derived = deriveAbsDisclosedQty(c, quoteLotSize);
    setLocalText((prev) => ({
      ...prev,
      qtyLots: c,
      ...(derived !== "" && !userEditedAbsDisQtyRef.current ? { absDisQty: derived } : {}),
    }));
    if (isControlled) {
      onSelectionChange(scriptIndex, "qtyLots", c);
      if (derived !== "" && !userEditedAbsDisQtyRef.current) {
        onSelectionChange(scriptIndex, "absDisQty", derived);
      }
    } else {
      setLocalState((prev) => ({
        ...prev,
        qtyLots: c,
        ...(derived !== "" && !userEditedAbsDisQtyRef.current ? { absDisQty: derived } : {}),
      }));
    }
  }, [defaultQtyLots, qtyLots, isControlled, scriptIndex, onSelectionChange, exchangeValue, quoteLotSize]);

  useEffect(() => {
    if (defaultAbsDisQty == null || defaultAbsDisQty === "") return;
    if (absDisQty !== "") return;
    if (userClearedAbsDisQtyRef.current || userEditedAbsDisQtyRef.current) return;
    setAbsDisQty(defaultAbsDisQty);
  }, [defaultAbsDisQty, absDisQty]);

  /* Lot-size driven disclosed qty: keep deriving when locked; respect user edits when editable. */
  useEffect(() => {
    if (!usesLotSizeQuantity(quoteLotSize) && userEditedAbsDisQtyRef.current) return;
    const t = String(qtyLots || "").trim();
    if (t === "") return;
    const derived = deriveAbsDisclosedQty(t, quoteLotSize);
    if (derived === "") return;
    userClearedAbsDisQtyRef.current = false;
    setProp("absDisQty", derived);
    setLocalText((prev) => (prev.absDisQty === derived ? prev : { ...prev, absDisQty: derived }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quoteLotSize, exchangeValue]);

  const productOptionsForExchange = useMemo(() => {
    if (isDerivativesExchange(displayedExchange || exchangeValue)) {
      return PRODUCT_OPTIONS.filter((o) => o !== "MARGIN");
    }
    return PRODUCT_OPTIONS;
  }, [displayedExchange, exchangeValue]);

  const orderTypeOptionsForExchange = useMemo(() => ORDER_TYPE_UI_OPTIONS, []);

  const productSelectDisplayValue = useMemo(() => {
    const opts = productOptionsForExchange;
    if (!opts.length) return productType || "";
    if (productType && opts.includes(productType)) return productType;
    return opts[0] ?? "";
  }, [productOptionsForExchange, productType]);

  useEffect(() => {
    const opts = productOptionsForExchange;
    if (!opts.length) return;
    if (!productType || !opts.includes(productType)) {
      const first = opts[0];
      if (first != null && productType !== first) setProductType(first);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productType, productOptionsForExchange]);

  useEffect(() => {
    const opts = orderTypeOptionsForExchange;
    if (!opts.length) return;
    const current = orderType || "";
    if (!current || !opts.includes(current)) {
      setOrderType(opts[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderType, orderTypeOptionsForExchange]);

  const handleProductArrowKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const next = cycleOptionList(productOptionsForExchange, productSelectDisplayValue, e.key);
    if (next !== undefined && next !== productSelectDisplayValue) {
      e.preventDefault();
      e.stopPropagation();
      setProductType(next);
    }
  };

  const orderTypeSelectValue = orderTypeOptionsForExchange.includes(orderType)
    ? orderType
    : orderTypeOptionsForExchange[0] || "";
  const handleOrderTypeArrowKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    const next = cycleOptionList(orderTypeOptionsForExchange, orderTypeSelectValue, e.key);
    if (next !== undefined && next !== orderTypeSelectValue) {
      e.preventDefault();
      e.stopPropagation();
      setOrderType(next);
    }
  };

  const validitySelectValue = validity || validityOptions[0] || "";
  const handleValidityArrowKey = (e) => {
    if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;
    if (!validityOptions?.length || validityOptions.length < 2) return;
    const next = cycleOptionList(validityOptions, validitySelectValue, e.key);
    if (next !== undefined && next !== validitySelectValue) {
      e.preventDefault();
      e.stopPropagation();
      setValidity(next);
    }
  };

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key === "Tab") {
        document.body.dataset.scriptTabFocus = "true";
      }
    };
    const onPointerDown = () => {
      delete document.body.dataset.scriptTabFocus;
    };
    scriptTabFocusBodyListenerCount += 1;
    if (scriptTabFocusBodyListenerCount === 1) {
      document.addEventListener("keydown", onKeyDown, true);
      document.addEventListener("pointerdown", onPointerDown, true);
    }
    return () => {
      scriptTabFocusBodyListenerCount -= 1;
      delete document.body.dataset.scriptTabFocus;
      if (scriptTabFocusBodyListenerCount === 0) {
        document.removeEventListener("keydown", onKeyDown, true);
        document.removeEventListener("pointerdown", onPointerDown, true);
      }
    };
  }, []);

  return (
    <div className={`${styles.scriptWrapper} ${animateIn ? styles.scriptEnter : ""}`}>
        <div className={`${styles.scriptContainer} ${transactionType === "Buy" ? styles.scriptContainerBuy : styles.scriptContainerSell}`}>
          <div className={styles.headerBar}>
            <div className={styles.headerLeft}>
              <Checkbox
                checked={headerSelected}
                onChange={(e) => setHeaderSelected(e.target.checked)}
                size="small"
                className={styles.headerCheckbox}
              />
              <span className={styles.headerQuote}>QUOTE</span>
              <span className={styles.headerSeparator}>|</span>
              <span className={styles.headerSymbol}>{displayHeaderSymbol}</span>
              <span className={styles.headerSeparator}>|</span>
              <span className={styles.headerMargin}>{displayMargin}</span>
            </div>
            {onDelete && (
              <button type="button" className={styles.headerDelete} aria-label="Delete script" onClick={() => onDelete(id, scriptIndex)}>
                <GlobalIcon iconName="ScriptDeleteIcon" className={styles.headerDeleteIcon} />
              </button>
            )}
          </div>

          <div className={`${styles.scriptBody} ${transactionType === "Buy" ? styles.scriptBodyBuy : styles.scriptBodySell}`}>
            <div className={styles.paramsRow}>
              {SCRIP_DROPDOWN_FIELDS.map(({ label, field }) => {
                const fieldDisabled =
                  (field === "strike" && strikeDisabled) ||
                  (field === "expiry" && expiryDisabled) ||
                  (field === "category" && categoryDisabled);
                const fieldOptions = getOptionsForField(field);
                const fieldValue = fieldDisabled ? "" : getScripSelectValue(field);

                /* Trading Symbol / Strike: type-to-search on Tab focus */
                if (SEARCHABLE_SCRIP_FIELDS.has(field)) {
                  return (
                    <div key={field} className={styles.fieldGroup}>
                      <label className={styles.fieldLabel}>{label}</label>
                      <Autocomplete
                        size="small"
                        fullWidth
                        disabled={fieldDisabled}
                        options={fieldOptions}
                        value={fieldDisabled ? null : fieldValue || null}
                        onChange={(_e, newValue) => {
                          if (newValue != null && newValue !== "") {
                            handleScripChange(field, newValue);
                          }
                        }}
                        onInputChange={(_e, newInputValue, reason) => {
                          if (reason !== "input") return;
                          const exact = findExactScripOptionMatch(fieldOptions, newInputValue);
                          if (exact && exact !== fieldValue) {
                            handleScripChange(field, exact);
                          }
                        }}
                        openOnFocus
                        selectOnFocus
                        autoHighlight
                        autoSelect={Boolean(fieldValue)}
                        filterOptions={filterScripSearchOptions}
                        disableClearable={Boolean(fieldValue) && !fieldDisabled}
                        forcePopupIcon
                        popupIcon={<SelectDropdownIcon />}
                        noOptionsText="No options"
                        className={styles.symbolAutocomplete}
                        slotProps={{
                          paper: {
                            className: "symbolAutocompletePaper",
                          },
                          listbox: {
                            sx: {
                              py: 0.25,
                              "& .MuiAutocomplete-option": {
                                fontSize: "12px",
                                fontWeight: 400,
                                minHeight: "26px",
                                py: "4px",
                                px: "10px",
                                lineHeight: 1.35,
                              },
                            },
                          },
                          popupIndicator: {
                            disableRipple: true,
                            sx: {
                              p: 0,
                              m: 0,
                              color: "var(--text-muted)",
                              transform: "none !important",
                              "&.MuiAutocomplete-popupIndicatorOpen": {
                                transform: "none !important",
                              },
                              "& svg": {
                                width: 6,
                                height: 4,
                                display: "block",
                              },
                            },
                          },
                        }}
                        sx={{
                          height: 22,
                          "& .MuiOutlinedInput-root": {
                            height: 22,
                            minHeight: "22px !important",
                            maxHeight: 22,
                            py: 0,
                            pl: 0,
                            pr: "22px !important",
                          },
                          "& .MuiAutocomplete-input": {
                            p: "2px 8px !important",
                            height: 22,
                            fontSize: 12,
                            lineHeight: "18px",
                          },
                          "& .MuiOutlinedInput-notchedOutline": {
                            border: "none",
                          },
                          "& .MuiAutocomplete-endAdornment": {
                            right: 7,
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            size="small"
                            placeholder={fieldDisabled ? "—" : "Type to search"}
                            inputProps={{
                              ...params.inputProps,
                              "aria-label": label,
                            }}
                            InputProps={{
                              ...params.InputProps,
                              sx: {
                                height: 22,
                                minHeight: "22px !important",
                                py: 0,
                              },
                            }}
                          />
                        )}
                      />
                    </div>
                  );
                }

                return (
                  <div key={field} className={styles.fieldGroup}>
                    <label className={styles.fieldLabel}>{label}</label>
                    <FormControl size="small" fullWidth>
                      <Select
                        IconComponent={SelectDropdownIcon}
                        disabled={fieldDisabled}
                        displayEmpty
                        value={fieldValue}
                        renderValue={
                          fieldDisabled
                            ? () => "—"
                            : (v) => (v == null || v === "" ? "" : v)
                        }
                        onChange={(e) => handleScripChange(field, e.target.value)}
                        onKeyDown={(e) => {
                          if (fieldDisabled) return;
                          handleScripArrowKey(field, e);
                        }}
                        className={styles.select}
                        MenuProps={mergeSelectMenuPropsWithArrow((e) => handleScripArrowKey(field, e))}
                      >
                        {fieldDisabled ? (
                          <MenuItem value="" disabled>
                            —
                          </MenuItem>
                        ) : (
                          [
                            ...(fieldValue === ""
                              ? [
                                  <MenuItem key="__empty" value="" disabled>
                                    {" "}
                                  </MenuItem>,
                                ]
                              : []),
                            ...fieldOptions.map((opt) => (
                              <MenuItem key={opt} value={opt}>
                                {opt}
                              </MenuItem>
                            )),
                          ]
                        )}
                      </Select>
                    </FormControl>
                  </div>
                );
              })}
            </div>

            <div className={styles.quoteGrid}>
              <div className={styles.quoteGridInner}>
                {renderQuoteMetrics(quoteMetrics)}
              </div>
            </div>

            <div className={styles.transactionRow}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Transaction Type</label>
                <div className={styles.buySellGroup}>
                  <button
                    type="button"
                    className={`${styles.buySellBtn} ${transactionType === "Buy" ? styles.buySellActive : ""}`}
                    onClick={() => setTransactionType("Buy")}
                  >
                    Buy
                  </button>
                  <button
                    type="button"
                    className={`${styles.buySellBtn} ${transactionType === "Sell" ? styles.buySellActive : ""}`}
                    onClick={() => setTransactionType("Sell")}
                  >
                    Sell
                  </button>
                </div>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Qty/Lots</label>
                <TextField
                  size="small"
                  value={qtyLots}
                  onChange={(e) => setQtyLots(e.target.value)}
                  onBlur={handleQtyLotsBlur}
                  className={styles.input}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Abs./Dis. Quantity</label>
                <TextField
                  size="small"
                  value={absDisQty}
                  onChange={(e) => setAbsDisQty(e.target.value)}
                  className={styles.input}
                  disabled={absDisQtyDisabledForLots}
                  title={absDisQtyDisabledForLots ? "Not editable when lot size > 1 — value follows lots × lot size" : undefined}
                />
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Product Type</label>
                <FormControl size="small" fullWidth>
                  <Select
                    IconComponent={SelectDropdownIcon}
                    value={productSelectDisplayValue}
                    onChange={(e) => setProductType(e.target.value)}
                    onKeyDown={handleProductArrowKey}
                    className={styles.select}
                    MenuProps={mergeSelectMenuPropsWithArrow(handleProductArrowKey)}
                  >
                    {productOptionsForExchange.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Order Type</label>
                <FormControl size="small" fullWidth>
                  <Select
                    IconComponent={SelectDropdownIcon}
                    value={orderTypeSelectValue}
                    onChange={(e) => setOrderType(e.target.value)}
                    onKeyDown={handleOrderTypeArrowKey}
                    className={styles.select}
                    MenuProps={mergeSelectMenuPropsWithArrow(handleOrderTypeArrowKey)}
                  >
                    {orderTypeOptionsForExchange.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Price</label>
                <Tooltip
                  title={priceEmptyTooltipOpen ? "Price can't be empty" : circuitTooltipText}
                  placement="bottom"
                  arrow
                  disableHoverListener
                  disableFocusListener
                  disableTouchListener
                  open={priceEmptyTooltipOpen || (circuitTooltipOpen && Boolean(circuitTooltipText))}
                  slotProps={{
                    tooltip: {
                      sx: {
                        fontFamily: "Inter, sans-serif",
                        fontSize: "0.75rem",
                        maxWidth: 260,
                        bgcolor: "var(--bg-elevated)",
                        color: "var(--text-default)",
                        border: "1px solid var(--border-default)",
                        boxShadow: "0 6px 16px var(--shadow-key)",
                      },
                    },
                    arrow: {
                      sx: { color: "var(--bg-elevated)" },
                    },
                  }}
                >
                  <TextField
                    size="small"
                    value={price}
                    onChange={(e) => setPrice(e.target.value)}
                    onFocus={() => {
                      priceInputFocusedRef.current = true;
                      hidePriceEmptyTooltip();
                    }}
                    onBlur={handlePriceBlur}
                    className={`${styles.input}${priceEmptyTooltipOpen ? ` ${styles.inputError}` : ""}`}
                    inputProps={{ inputMode: "decimal" }}
                  />
                </Tooltip>
              </div>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>Validity</label>
                <FormControl size="small" fullWidth>
                  <Select
                    IconComponent={SelectDropdownIcon}
                    value={validity || validityOptions[0] || ""}
                    onChange={(e) => setValidity(e.target.value)}
                    onKeyDown={handleValidityArrowKey}
                    className={styles.select}
                    MenuProps={mergeSelectMenuPropsWithArrow(handleValidityArrowKey)}
                  >
                    {validityOptions.map((opt) => (
                      <MenuItem key={opt} value={opt}>{opt}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>
            </div>
          </div>

          <div className={styles.advancedRow}>
            <div className={styles.scheduleOrderPlusWrap}>
              <button type="button" className={styles.scheduleOrderPlusBtn} aria-label="Add script" onClick={() => onAddScript?.()}>
                <GlobalIcon iconName="AddIcon" className={styles.scheduleOrderPlusIcon} />
              </button>
            </div>
          </div>
        </div>

        <div className={styles.orderBookRight}>
          <div className={styles.orderBookTablesRow}>
            <div className={styles.orderBookTableBuy}>
              <div className={styles.orderBookHeader}>
                <span>ORDER</span>
                <span>QUANTITY</span>
                <span>PRICE</span>
              </div>
              <div className={styles.orderBookBody}>
                {buyRows.map((row, i) => (
                  <div key={`buy-${i}-${row.price}`} className={styles.orderBookRow}>
                    <span>{row.order}</span>
                    <span>{row.quantity}</span>
                    <span>{row.price}</span>
                  </div>
                ))}
              </div>
              <div className={styles.orderBookFooter}>
                {bidFooter.map((m) => (
                  <div key={m.label} className={styles.orderBookFooterItem}>
                    <span className={styles.orderBookFooterLabel}>{m.label}</span>
                    <span className={styles.orderBookFooterValue}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className={styles.orderBookTableSell}>
              <div className={styles.orderBookHeader}>
                <span>PRICE</span>
                <span>QUANTITY</span>
                <span>ORDER</span>
              </div>
              <div className={styles.orderBookBody}>
                {sellRows.map((row, i) => (
                  <div key={`sell-${i}-${row.price}`} className={styles.orderBookRow}>
                    <span>{row.price}</span>
                    <span>{row.quantity}</span>
                    <span>{row.order}</span>
                  </div>
                ))}
              </div>
              <div className={styles.orderBookFooter}>
                {askFooter.map((m) => (
                  <div key={m.label} className={styles.orderBookFooterItem}>
                    <span className={styles.orderBookFooterLabel}>{m.label}</span>
                    <span className={styles.orderBookFooterValue}>{m.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
  );
}

export default React.memo(ScriptComponent);
